import { FC, useCallback, useMemo, useState } from 'react';
import { Col, Divider, Input, Row, Tabs, Typography } from 'antd';
import { TbArticle, TbArticleOff, TbDownload, TbList, TbProgressAlert, TbSearch } from 'react-icons/tb';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import { useEntitlements } from '@/hooks/useEntitlements';
import { useWorkspaceResourceFilters, type WorkspaceResourceFilterSetter } from '@/hooks/useWorkspaceResourceFilters';
import { SavedFilterResourceType, type AuditLogQueryMode } from '@/services/database';
import {
    ActivityTimelineSkeleton,
    EmptySection,
    FlexContentRegion,
    FullHeightFlexColumn,
    getAdvancedFilterCounts,
    LearnMoreFooter,
    VirtualizedList as List,
    OverflowAwareScrollEndFooter,
    PlanTag,
    SmallTag,
} from '@/shared';
import { useUserStore, useWorkspaceStore } from '@/stores';
import { MemberPermissionType } from '@/types';

import AuditLogExportDropdown from './AuditLogExportDropdown';
import AuditLogFiltersRow from './AuditLogFiltersRow';
import AuditLogListItem from './AuditLogListItem';
import { nestImmediateAuditLogs } from './auditLogNesting';
import { usePaginatedAuditLogs } from './hooks/usePaginatedAuditLogs';

type AuditLogTab = 'actionTaken' | 'allLogs';

const AuditLogs: FC = (): JSX.Element => {
    const [search, setSearch] = useState('');
    const [activeTab, setActiveTab] = useState<AuditLogTab>('allLogs');

    const { selectedUser: user } = useUserStore();
    const { selectedWorkspace: workspace } = useWorkspaceStore();
    const { token } = useAntDesignTheme();
    const { hasFeature } = useEntitlements();
    const { advancedFilter, filters, setAdvancedFilter, setFilterDefinition, setFilters } = useWorkspaceResourceFilters(
        {
            resourceId: workspace?.id,
            resourceType: SavedFilterResourceType.AUDIT_LOG,
        },
    );

    const isEnterprise = hasFeature('auditLogs');
    const canManageSavedFilters = [MemberPermissionType.OWNER, MemberPermissionType.ADMIN].includes(
        user?.activeMember?.permission as MemberPermissionType,
    );
    const mode: AuditLogQueryMode = activeTab === 'actionTaken' ? 'actionTaken' : 'all';
    const {
        actionTakenTotal,
        auditLogs,
        facets,
        filterOptionLogs,
        hasMore,
        loadMore,
        loading,
        loadingMore,
        total,
        updateMember,
    } = usePaginatedAuditLogs({
        advancedFilter,
        enabled: isEnterprise && canManageSavedFilters,
        filters,
        mode,
        search,
        workspaceId: workspace?.id ?? user?.activeWorkspace?.id,
    });
    const activeFilterCount =
        filters.length + getAdvancedFilterCounts(advancedFilter).conditions + (search.trim() ? 1 : 0);
    const nestedAuditLogs = useMemo(() => nestImmediateAuditLogs(auditLogs), [auditLogs]);
    const handleFiltersChange = useCallback<WorkspaceResourceFilterSetter>(
        (nextFilters, options) => setFilters(nextFilters, options),
        [setFilters],
    );

    const emptyState = (label: string, description: string) => (
        <EmptySection
            description={description}
            height='100%'
            icon={<TbArticleOff size='1.25rem' style={{ color: token.colorTextTertiary }} />}
            label={label}
        />
    );

    const renderAuditLogList = (emptyLabel: string, emptyDescription: string) => (
        <div
            data-audit-log-list-shell='true'
            style={{
                boxSizing: 'border-box',
                height: '100%',
                minHeight: 0,
                padding: '0 0.75rem',
                width: '100%',
            }}
        >
            {auditLogs.length !== 0 && !loading ? (
                <List
                    data-audit-log-list='true'
                    dataSource={nestedAuditLogs}
                    estimateSize={88}
                    getItemKey={({ log }) => log.id}
                    hasMore={hasMore}
                    height='100%'
                    split={false}
                    loadingMore={loadingMore}
                    loadMore={
                        loadingMore ? (
                            <div style={{ padding: '0.75rem 0.5rem' }}>
                                <ActivityTimelineSkeleton count={2} density='compact' showMetadataAction />
                            </div>
                        ) : !hasMore ? (
                            <OverflowAwareScrollEndFooter
                                data-audit-log-scroll-end-footer='true'
                                label='End of Audit Logs'
                            />
                        ) : null
                    }
                    onEndReached={loadMore}
                    innerStyle={{ width: '100%' }}
                    renderItem={({ depth, log }, index) => (
                        <AuditLogListItem
                            key={log.id}
                            filters={filters}
                            isLast={index === nestedAuditLogs.length - 1}
                            log={log}
                            nestingDepth={depth}
                            nextNestingDepth={nestedAuditLogs[index + 1]?.depth ?? 0}
                            onFiltersChange={handleFiltersChange}
                            onMemberUpdated={updateMember}
                        />
                    )}
                    style={{ width: '100%' }}
                />
            ) : loading ? (
                <div style={{ height: '100%', overflow: 'auto' }}>
                    <ActivityTimelineSkeleton
                        count={7}
                        density='compact'
                        showMetadataAction
                        style={{ minHeight: '100%', padding: '0.375rem 0.5rem' }}
                    />
                </div>
            ) : activeFilterCount ? (
                emptyState(
                    'No Audit Logs Match These Filters',
                    'No workspace events match the current search or filters.',
                )
            ) : (
                emptyState(emptyLabel, emptyDescription)
            )}
        </div>
    );

    return (
        <FullHeightFlexColumn className='audit-logs-layout'>
            <style>
                {`
                    .audit-log-tabs {
                        display: flex;
                        flex-direction: column;
                        height: 100%;
                        min-height: 0;
                    }

                    .audit-log-tabs > .ant-tabs-nav {
                        flex: none;
                        margin-bottom: 0 !important;
                    }

                    .audit-log-tabs > .ant-tabs-content-holder,
                    .audit-log-tabs > .ant-tabs-content-holder > .ant-tabs-content,
                    .audit-log-tabs > .ant-tabs-content-holder > .ant-tabs-content > .ant-tabs-tabpane {
                        height: 100%;
                        min-height: 0;
                    }

                    .audit-log-tabs > .ant-tabs-content-holder {
                        flex: 1 1 0%;
                        overflow: hidden;
                    }
                `}
            </style>
            <Row align='top' style={{ flex: 'none' }} wrap={false}>
                <Col flex='auto'>
                    <Typography.Title level={4}>
                        <TbDownload style={{ verticalAlign: '-0.2rem', marginRight: '0.5rem' }} />
                        Export Logs
                    </Typography.Title>
                    <Typography.Paragraph style={{ color: token.colorTextTertiary, marginBottom: 0 }}>
                        Download filtered workspace events for compliance reviews, investigations, and recordkeeping.
                    </Typography.Paragraph>
                </Col>
                {isEnterprise && canManageSavedFilters && (
                    <Col flex='none' style={{ alignSelf: 'center', textAlign: 'right' }}>
                        <AuditLogExportDropdown
                            advancedFilter={advancedFilter}
                            filters={filters}
                            resultCount={total}
                            search={search}
                            workspaceId={workspace?.id ?? user?.activeWorkspace?.id}
                            workspaceName={workspace?.name}
                        />
                    </Col>
                )}
            </Row>
            <Divider style={{ flex: 'none', margin: '1rem 0' }} />
            <div style={{ flex: 'none' }}>
                <Typography.Title level={4}>
                    <TbArticle style={{ verticalAlign: '-0.2rem', marginRight: '0.5rem' }} />
                    Audit Logs
                    {!isEnterprise && (
                        <PlanTag
                            plan='enterprise'
                            style={{
                                verticalAlign: '0.1rem',
                                marginLeft: '0.5rem',
                            }}
                        />
                    )}
                </Typography.Title>
                <Typography.Paragraph style={{ color: token.colorTextTertiary }}>
                    Monitor all workspace security, access, and resource events.
                </Typography.Paragraph>
            </div>
            {isEnterprise && (
                <>
                    <Row align='middle' style={{ width: '100%' }}>
                        <Input
                            value={search}
                            onChange={(event) => setSearch(event.target.value)}
                            prefix={
                                <TbSearch
                                    size='1rem'
                                    style={{ marginRight: '0.5rem', color: token.colorTextQuaternary }}
                                />
                            }
                            allowClear
                            placeholder='Search Audit Logs...'
                        />
                    </Row>
                    <Row align='middle' style={{ width: '100%' }}>
                        <Col span={24}>
                            <AuditLogFiltersRow
                                advancedFilter={advancedFilter}
                                auditLogs={filterOptionLogs}
                                canManageSharedFilters={canManageSavedFilters}
                                facets={facets}
                                filters={filters}
                                onAdvancedChange={setAdvancedFilter}
                                onChange={handleFiltersChange}
                                onDefinitionChange={setFilterDefinition}
                                resourceId={workspace?.id}
                            />
                        </Col>
                    </Row>
                    <FlexContentRegion
                        data-audit-log-surface='true'
                        style={{
                            border: `1px dashed ${token.colorBorder}`,
                            borderRadius: token.borderRadius,
                        }}
                    >
                        <Tabs
                            activeKey={activeTab}
                            className='audit-log-tabs'
                            items={[
                                {
                                    key: 'allLogs',
                                    label: (
                                        <span>
                                            <TbList
                                                size='1rem'
                                                style={{ verticalAlign: '-0.2rem', marginRight: '0.375rem' }}
                                            />
                                            All Logs
                                            {total !== 0 && <SmallTag message={total >= 99 ? '99+' : total} />}
                                        </span>
                                    ),
                                    children: renderAuditLogList(
                                        'No Audit Logs',
                                        'Workspace security, access, and resource events will appear here.',
                                    ),
                                },
                                {
                                    key: 'actionTaken',
                                    label: (
                                        <span>
                                            <TbProgressAlert
                                                size='1rem'
                                                style={{ verticalAlign: '-0.2rem', marginRight: '0.375rem' }}
                                            />
                                            Action Taken
                                            {actionTakenTotal !== 0 && (
                                                <SmallTag message={actionTakenTotal >= 99 ? '99+' : actionTakenTotal} />
                                            )}
                                        </span>
                                    ),
                                    children: renderAuditLogList(
                                        'No Audit Logs Taken Action On',
                                        'Workspace events with a recorded action will appear here.',
                                    ),
                                },
                            ]}
                            onChange={(key) => {
                                if (key === 'allLogs' || key === 'actionTaken') {
                                    setActiveTab(key);
                                }
                            }}
                            size='small'
                            tabBarStyle={{ margin: 0, padding: '0 0.75rem' }}
                        />
                    </FlexContentRegion>
                    <div data-audit-log-footer='true' style={{ flex: 'none' }}>
                        <Divider style={{ margin: '1rem 0' }} />
                        <LearnMoreFooter topic='audit logs' />
                    </div>
                </>
            )}
        </FullHeightFlexColumn>
    );
};

export default AuditLogs;
